import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { PageTitle } from "../../components/Common/PageTitle";
import InternalLabelEditModal from "../../components/InternalLabelsManagementComponents/InternalLabelEditModal/InternalLabelEditModal";
import InternalLabelsList from "../../components/InternalLabelsManagementComponents/InternalLabelsList/InternalLabelsList";
import { PAGE_NUMBER_OPTIONS } from "../../config";
import { InternalLabelData } from "../../models/InternalLabelData";
import DataSvc from "../../services/dataSvc";

import "./styles.scss";

const defaultFilterFormData = {
  searchName: "",
  sort: "id",
  order: undefined,
  numberPerPage: PAGE_NUMBER_OPTIONS[0], // Default to the first option
  pageIndex: 1,
};

const InternalLabelsManagementPage: React.FC = () => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`internalLabelsManagementPage.${key}`);

  const [filterFormData, setFilterFormData] = useState<any>(
    defaultFilterFormData
  );

  const { data: internalLabels, refetch } = useQuery(
    ["internalLabels", filterFormData],
    () =>
      DataSvc.getInternalLabels({
        offset: (filterFormData.pageIndex - 1) * filterFormData.numberPerPage,
        limit: filterFormData.numberPerPage,
        seek_total_count: true,
        query: filterFormData.searchName.trim(),
      })
  );

  const [editingLabel, setEditingLabel] = useState<InternalLabelData | null>(
    null
  );
  const [showEditModal, setShowEditModal] = useState(false);

  const handleSave = async (label: Partial<InternalLabelData>) => {
    try {
      if (label.id) {
        await DataSvc.updateInternalLabel(label, Number(label.id));
      } else {
        await DataSvc.createInternalLabel(label);
      }
      setShowEditModal(false);
      setEditingLabel(null);
      refetch();
    } catch (error) {
      // Do nothing, axios will handle the error
    }
  };

  const handleDelete = async (labelId: number) => {
    await DataSvc.deleteInternalLabel(labelId);
    setShowEditModal(false);
    setEditingLabel(null);
    refetch();
  };

  const handleUpdateInternalLabelLocations = async (
    labelId: string | number,
    addIds: string[],
    deleteIds: string[]
  ) => {
    await DataSvc.batchOperationInternalLabels({
      internal_label_id: String(labelId),
      add_location_ids: addIds,
      delete_location_ids: deleteIds,
    });
    refetch();
  };

  return (
    <>
      <div className="right-content internal-labels-management">
        <div className="top-title-bar flex-grid">
          <PageTitle text={t("title")} />
          <button
            className="btn btn-blue"
            onClick={() => {
              setEditingLabel(null);
              setShowEditModal(true);
            }}
          >
            {t("add_label")}
          </button>
          <div className="line"></div>
        </div>
        <div className="white-border-panel">
          <InternalLabelsList
            internalLabels={internalLabels?.data || []}
            onEdit={(label) => {
              setEditingLabel(label);
              setShowEditModal(true);
            }}
            filterFormData={filterFormData}
            totalCount={internalLabels?.count || 0}
            onChangeFilterFormData={setFilterFormData}
            onUpdateAssignLabels={async (label, result) => {
              const { addIds = [], deleteIds = [] } = result || {};
              await handleUpdateInternalLabelLocations(
                label.id,
                addIds,
                deleteIds
              );
            }}
          />
        </div>

        {showEditModal && (
          <InternalLabelEditModal
            label={editingLabel}
            onClose={() => {
              setShowEditModal(false);
              setEditingLabel(null);
            }}
            onSave={handleSave}
            onDelete={async (labelId: number) => {
              await handleDelete(labelId);
              setShowEditModal(false);
              setEditingLabel(null);
            }}
          />
        )}
      </div>
    </>
  );
};

export default InternalLabelsManagementPage;
